import React, { useState, useEffect } from 'react';
import { Clock, Copy, Check, RefreshCw, Calendar } from 'lucide-react';

const TimestampConverter: React.FC = () => {
  const [unixTimestamp, setUnixTimestamp] = useState('');
  const [humanDate, setHumanDate] = useState('');
  const [humanTime, setHumanTime] = useState('');
  const [timezone, setTimezone] = useState('local');
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [copied, setCopied] = useState<string | null>(null);
  const [format, setFormat] = useState<'seconds' | 'milliseconds'>('seconds');

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const parseTimestamp = (ts: string): Date | null => {
    const num = parseInt(ts);
    if (isNaN(num)) return null;
    // Auto-detect seconds vs milliseconds
    if (num > 1e12) return new Date(num);
    return new Date(num * 1000);
  };

  const handleUnixChange = (value: string) => {
    setUnixTimestamp(value);
    const date = parseTimestamp(value);
    if (date && !isNaN(date.getTime())) {
      if (timezone === 'utc') {
        setHumanDate(date.toISOString().split('T')[0]);
        setHumanTime(date.toISOString().split('T')[1].slice(0, 8));
      } else {
        setHumanDate(date.toLocaleDateString('en-CA'));
        setHumanTime(date.toLocaleTimeString('en-GB'));
      }
    }
  };

  const handleDateTimeChange = (date: string, time: string) => {
    setHumanDate(date);
    setHumanTime(time);
    if (date && time) {
      let d: Date;
      if (timezone === 'utc') {
        d = new Date(`${date}T${time}Z`);
      } else {
        d = new Date(`${date}T${time}`);
      }
      if (!isNaN(d.getTime())) {
        const ts = format === 'milliseconds' ? d.getTime() : Math.floor(d.getTime() / 1000);
        setUnixTimestamp(ts.toString());
      }
    }
  };

  const setNow = () => {
    const now = new Date();
    const ts = format === 'milliseconds' ? now.getTime() : Math.floor(now.getTime() / 1000);
    setUnixTimestamp(ts.toString());
    handleUnixChange(ts.toString());
  };

  const copyToClipboard = async (text: string, type: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatCurrentTime = () => {
    const d = new Date(currentTime);
    return {
      unix: Math.floor(currentTime / 1000),
      unixMs: currentTime,
      iso: d.toISOString(),
      local: d.toLocaleString(),
      utc: d.toUTCString(),
    };
  };

  const current = formatCurrentTime();

  const commonTimestamps = [
    { label: 'Start of today', getValue: () => { const d = new Date(); d.setHours(0,0,0,0); return Math.floor(d.getTime()/1000); }},
    { label: 'Start of this week', getValue: () => { const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() - d.getDay()); return Math.floor(d.getTime()/1000); }},
    { label: 'Start of this month', getValue: () => { const d = new Date(); d.setHours(0,0,0,0); d.setDate(1); return Math.floor(d.getTime()/1000); }},
    { label: 'Start of this year', getValue: () => { const d = new Date(); d.setHours(0,0,0,0); d.setMonth(0,1); return Math.floor(d.getTime()/1000); }},
  ];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Timestamp Converter</h2>
        <p className="text-gray-600">Convert between Unix timestamps and human-readable dates</p>
      </div>

      <div className="max-w-3xl mx-auto space-y-6">
        {/* Current Time Display */}
        <div className="bg-gray-900 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400 text-sm flex items-center gap-2">
              <Clock className="w-4 h-4" /> Current Time
            </span>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-mono text-green-400">{current.unix}</span>
              <button
                onClick={() => copyToClipboard(current.unix.toString(), 'current')}
                className="p-1 text-gray-400 hover:text-white"
              >
                {copied === 'current' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">ISO 8601:</span>
              <p className="font-mono text-gray-300">{current.iso}</p>
            </div>
            <div>
              <span className="text-gray-500">Local:</span>
              <p className="font-mono text-gray-300">{current.local}</p>
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="flex gap-4 justify-center">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Format:</label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value as 'seconds' | 'milliseconds')}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
            >
              <option value="seconds">Seconds</option>
              <option value="milliseconds">Milliseconds</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Timezone:</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
            >
              <option value="local">Local</option>
              <option value="utc">UTC</option>
            </select>
          </div>
        </div>

        {/* Converter */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Unix Timestamp */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unix Timestamp ({format})
            </label>
            <div className="relative">
              <input
                type="text"
                value={unixTimestamp}
                onChange={(e) => handleUnixChange(e.target.value)}
                className="w-full px-4 py-3 text-lg font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                placeholder={format === 'seconds' ? '1706140800' : '1706140800000'}
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                <button
                  onClick={setNow}
                  className="p-2 text-gray-500 hover:text-gray-700"
                  title="Set to now"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => copyToClipboard(unixTimestamp, 'unix')}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  {copied === 'unix' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Human Date/Time */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Human Readable ({timezone === 'utc' ? 'UTC' : 'Local'})
            </label>
            <div className="flex gap-2">
              <input
                type="date"
                value={humanDate}
                onChange={(e) => handleDateTimeChange(e.target.value, humanTime)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
              />
              <input
                type="time"
                step="1"
                value={humanTime}
                onChange={(e) => handleDateTimeChange(humanDate, e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
              />
            </div>
          </div>
        </div>

        {/* Quick Timestamps */}
        <div className="bg-gray-50 rounded-xl p-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">Quick Timestamps</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {commonTimestamps.map((item) => (
              <button
                key={item.label}
                onClick={() => handleUnixChange(item.getValue().toString())}
                className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-100 transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="bg-rose-50 rounded-xl p-4 border border-rose-100">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-rose-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-rose-900 mb-2">About Unix Timestamps</h4>
              <p className="text-sm text-rose-700">
                Unix time is the number of seconds since January 1, 1970 (UTC), also known as the Unix Epoch.
                It's widely used in programming and databases for storing dates.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimestampConverter;
